// ─────────────────────────────────────────────────────────────────────────────
// AXIS360 CAREER INTEREST PROFILE — Scoring Engine
// Scale: 0-4 per item, 8 items per dimension, simple average composite.
// Produces a 3-letter "Holland Code" (the standard convention in RIASEC-based
// career interest assessment: the three highest-scoring dimensions, ranked),
// per-dimension percentages, and a consistency indicator (how close together
// the top dimensions score — a tightly-clustered top 3 is a more "consistent"
// and typically more stable interest pattern than a widely-spread one).
// ─────────────────────────────────────────────────────────────────────────────
import { RIASEC_ITEMS, RIASEC_DIMS } from "./questionBank.js";

export function computeRIASEC(answers) {
  const dimScores = { R:0, I:0, A:0, S:0, E:0, C:0 };
  const dimCounts = { R:0, I:0, A:0, S:0, E:0, C:0 };

  RIASEC_ITEMS.forEach((item, i) => {
    const raw = answers[i];
    if (raw === null || raw === undefined) return;
    dimScores[item.dim] += Number(raw);
    dimCounts[item.dim]++;
  });

  const dimResults = {};
  Object.keys(RIASEC_DIMS).forEach(dim => {
    const raw = dimScores[dim];
    const max = dimCounts[dim] * 4;
    const pct = max > 0 ? Math.round((raw / max) * 100) : 0;
    dimResults[dim] = { raw, max, pct };
  });

  // Rank all 6 dimensions by score, descending
  const ranked = Object.entries(dimResults)
    .map(([dim, r]) => ({ dim, ...r }))
    .sort((a, b) => b.pct - a.pct);

  const hollandCode = ranked.slice(0, 3).map(r => r.dim).join("");
  const primary = ranked[0]?.dim;
  const secondary = ranked[1]?.dim;
  const tertiary = ranked[2]?.dim;

  // Consistency: how close together the top 3 scores are. A gap of <15 points
  // between 1st and 3rd suggests a genuinely blended, consistent interest
  // pattern; a large gap suggests one or two dominant interests with the
  // rest trailing well behind.
  const topGap = ranked[0] && ranked[2] ? ranked[0].pct - ranked[2].pct : 0;
  const consistency = topGap <= 15 ? "high" : topGap <= 30 ? "moderate" : "low";

  const answered = Object.values(dimCounts).reduce((t, n) => t + n, 0);
  const pct = Object.fromEntries(Object.entries(dimResults).map(([k, v]) => [k, v.pct]));
  const interpretation = interpretRIASEC(pct);

  return { raw: dimScores, pct,
    norm: Object.fromEntries(Object.entries(dimResults).map(([k,v])=>[k, +(v.pct/10).toFixed(1)])),
    dimResults, ranked, hollandCode, primary, secondary, tertiary, consistency, topGap,
    answered, complete: answered === RIASEC_ITEMS.length, interpretation };
}

// ── Interpretation layer ─────────────────────────────────────────────────────
// A pure function of the six percentages, so reports can derive it for results stored before it existed.
// Thresholds are practitioner heuristics (points of "% of maximum"), not norm-referenced cut-offs.
const HEX = ["R", "I", "A", "S", "E", "C"];                 // Holland's hexagon, in order
const ANGLE = { R: 0, I: 60, A: 120, S: 180, E: 240, C: 300 };

// Steps between two types around the hexagon: 1 adjacent, 2 alternate, 3 opposite.
export function hexDistance(a, b) {
  const d = Math.abs(HEX.indexOf(a) - HEX.indexOf(b));
  return Math.min(d, 6 - d);
}

export const FLAT_SPREAD = 15;      // highest minus lowest below this: no clear primary interest
export const CLEAR_SPREAD = 35;     // at or above this: clearly differentiated
export const HIGH_ELEVATION = 75;   // mean at or above: rates nearly everything as enjoyable
export const LOW_ELEVATION = 25;    // mean at or below: rates little as enjoyable

export function interpretRIASEC(pct) {
  const vals = HEX.map(d => Number(pct?.[d]) || 0);
  const mean = vals.reduce((t, v) => t + v, 0) / 6;
  const max = Math.max(...vals), min = Math.min(...vals);
  const spread = max - min;

  // Same ordering the Holland code uses: score descending, hexagon order on ties.
  const order = [...HEX].sort((a, b) => (Number(pct?.[b]) || 0) - (Number(pct?.[a]) || 0));
  const v = d => Number(pct?.[d]) || 0;
  const tied = (i, j) => v(order[i]) === v(order[j]);
  // The third letter is arbitrary when the 3rd and 4th scores are equal; the order of the first three is when any two are equal.
  const codeBoundaryTie = tied(2, 3);
  const orderTie = tied(0, 1) || tied(1, 2);

  const differentiation = spread >= CLEAR_SPREAD ? "clear" : spread >= FLAT_SPREAD ? "moderate" : "flat";
  const elevation = mean >= HIGH_ELEVATION ? "high" : mean <= LOW_ELEVATION ? "low" : "typical";

  // Holland's consistency: how close the two highest types sit on the hexagon. It is only meaningful when
  // those two are actually the top two: not for a flat profile, nor when 2nd and 3rd place are tied.
  const dist = hexDistance(order[0], order[1]);
  const hexConsistency = differentiation === "flat" || tied(1, 2) ? null
    : dist === 1 ? "high" : dist === 2 ? "moderate" : "low";

  // Position on Prediger's two underlying axes: the profile's centre of mass on the hexagon, using each
  // area's score relative to the person's own average so that rating everything high does not move it.
  // Positive = Things / Ideas, negative = People / Data. Units: points of "% of maximum".
  let tp = 0, di = 0;
  HEX.forEach(d => {
    const dev = v(d) - mean, rad = ANGLE[d] * Math.PI / 180;
    tp += dev * Math.cos(rad);
    di += dev * Math.sin(rad);                      // I (60°) and A (120°) are the Ideas side; E, C the Data side
  });
  const thingsPeople = +(tp / 3).toFixed(1);        // R = +1 … S = −1
  const ideasData = +(di / 3).toFixed(1);           // I, A positive … E, C negative
  const lean = x => Math.abs(x) < 4 ? "balanced" : Math.abs(x) < 10 ? "slight" : Math.abs(x) < 20 ? "moderate" : "strong";

  const notes = [];
  if (differentiation === "flat") notes.push("flat");
  if (elevation === "high") notes.push("high_elevation");
  if (elevation === "low") notes.push("low_elevation");
  if (spread === 0) notes.push("all_equal");
  else if (codeBoundaryTie) notes.push("code_tie");
  else if (orderTie) notes.push("order_tie");

  return { mean: +mean.toFixed(1), spread, differentiation, elevation,
    hexConsistency, hexDistance: dist, codeBoundaryTie, orderTie,
    tiedAtBoundary: codeBoundaryTie ? [order[2], order[3]] : null,
    deviation: Object.fromEntries(HEX.map(d => [d, +(v(d) - mean).toFixed(1)])),
    thingsPeople, ideasData,
    thingsPeopleLean: lean(thingsPeople), ideasDataLean: lean(ideasData),
    notes };
}
